import { useCallback, useRef, useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Modal, useModalTitleId } from './Modal';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
}

interface DialogState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

/* ------------------------------------------------------------------ */
/*  Hook — useConfirmDialog                                            */
/* ------------------------------------------------------------------ */

/**
 * Promise-based confirm dialog that replaces native `window.confirm()`.
 *
 * ```tsx
 * const [dialog, confirm] = useConfirmDialog();
 *
 * async function handleDelete() {
 *   const ok = await confirm({
 *     title: 'Delete item?',
 *     description: 'This action cannot be undone.',
 *     confirmLabel: 'Delete',
 *     variant: 'danger',
 *   });
 *   if (!ok) return;
 *   deleteMutation.mutate();
 * }
 *
 * return <>{content}{dialog}</>
 * ```
 */
export function useConfirmDialog(): [
  dialog: React.ReactNode,
  confirm: (options: ConfirmOptions) => Promise<boolean>,
] {
  const [state, setState] = useState<DialogState | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        setState({ ...options, resolve });
      }),
    [],
  );

  const handleClose = useCallback(
    (value: boolean) => {
      state?.resolve(value);
      setState(null);
    },
    [state],
  );

  const dialog = state ? (
    <ConfirmDialog
      open
      title={state.title}
      description={state.description}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      variant={state.variant}
      onConfirm={() => handleClose(true)}
      onCancel={() => handleClose(false)}
    />
  ) : null;

  return [dialog, confirm];
}

/* ------------------------------------------------------------------ */
/*  Component — ConfirmDialog                                          */
/* ------------------------------------------------------------------ */

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
}

/** Carries the id <Modal> labels itself by — without it the dialog is unnamed. */
function ConfirmTitle({ children }: { children: string }) {
  return <h2 id={useModalTitleId()} className="text-sm font-semibold text-ink">{children}</h2>;
}

function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the confirm button when opened
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  const isDanger = variant === 'danger';

  return (
    <Modal open={open} onClose={onCancel} size="sm">
      <Modal.Body>
        <ConfirmTitle>{title}</ConfirmTitle>
        <p className="mt-1.5 whitespace-pre-line text-2xs leading-relaxed text-ink-muted">
          {description}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border px-3.5 py-1.5 text-2xs font-medium text-ink-muted transition-colors hover:bg-surface-1 hover:text-ink"
        >
          {cancelLabel}
        </button>
        <button
          ref={confirmRef}
          type="button"
          onClick={onConfirm}
          className={clsx(
            'rounded-lg px-3.5 py-1.5 text-2xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-1',
            isDanger
              ? 'bg-danger text-on-fill hover:bg-danger/90 focus:ring-danger/40'
              : 'bg-primary text-white hover:bg-primary/90 focus:ring-primary/40',
          )}
        >
          {confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export { ConfirmDialog };
